import { type ColumnDef, type Row } from '@tanstack/react-table';
import { IndeterminateCheckbox } from '@/components/data-table/data-table-checkbox';
import { ColumnType } from '@/types/column';
import { GalleryView } from '@/types';
import { cn } from '@/lib/utils';

interface SelectColumnOptions<T> {
  id?: string;
  accessorFn?: (row: T) => string;
  accessorKey?: keyof T;
  enableSorting?: boolean;
  enableHiding?: boolean;
  minSize?: number;
  maxSize?: number;
  size?: number;
  cellClassName?: string;
  headerClassName?: string;
  galleryView?: GalleryView;
  dataCy?: string;
  onClick?: (e: MouseEvent, row: Row<T>) => void;
  hideHeaderCheckbox?: boolean;
}

export const SelectColumn = <T extends Record<string, any>>({
  id,
  accessorFn,
  accessorKey = ColumnType.Select as keyof T,
  size = 40,
  cellClassName,
  headerClassName,
  enableSorting = false,
  enableHiding = false,
  minSize = 40,
  maxSize = 40,
  galleryView,
  dataCy,
  onClick,
  hideHeaderCheckbox = false,
}: SelectColumnOptions<T> = {}): ColumnDef<T> => ({
  id,
  enableSorting,
  enableHiding,
  minSize,
  maxSize,
  accessorFn,
  accessorKey,
  header: ({ table }) =>
    hideHeaderCheckbox ? null : (
      <IndeterminateCheckbox
        className="mx-auto hover:cursor-default"
        {...{
          checked: table.getIsAllRowsSelected(),
          indeterminate: table.getIsSomeRowsSelected(),
        }}
      />
    ),
  meta: {
    cellClassName: cn('p-0! min-w-8', cellClassName),
    headerClassName: cn('p-0! min-w-8', headerClassName),
  },
  size,
  cell: ({ row }) => {
    const isSelected = row.getIsSelected();
    const isTileView = galleryView === GalleryView.TILE;

    return (
      <IndeterminateCheckbox
        checkboxContainerClassName={cn(
          'rounded-lg p-2 has-[:focus-visible]:bg-black/10! dark:has-[:focus-visible]:bg-white/10!',
          isTileView && 'group-hover/asset-container:flex!',
          isTileView && !isSelected && 'hidden touch:block',
        )}
        className={cn(
          'mx-auto',
          isTileView &&
            'ring-icon-overlay! drop-shadow-[1px_1px_2px_rgba(0,0,0,0.3)] icon-5! [&_span[data-state=checked]]:text-icon-overlay',
        )}
        checked={isSelected}
        disabled={!row.getCanSelect()}
        indeterminate={row.getIsSomeSelected()}
        onChange={row.getToggleSelectedHandler()}
        {...(onClick && {
          onClick: (e) => {
            e.stopPropagation();
            onClick(e as any, row);
          },
        })}
        data-cy={dataCy ?? `${galleryView ?? 'list'}-${row.index}-${ColumnType.Select}`}
        data-select-column="true"
      />
    );
  },
});
